export interface TexSvg {
  svg: string
  widthEx: number
  heightEx: number
  verticalAlignEx: number
}

import type { LiteElement } from 'mathjax-full/js/adaptors/lite/Element.js'

interface MathJaxEngine {
  convert(tex: string, display: boolean): TexSvg
}

let enginePromise: Promise<MathJaxEngine> | undefined

export async function texToSvg(
  tex: string,
  options: { display: boolean },
): Promise<TexSvg> {
  const engine = await loadMathJax()
  return engine.convert(tex, options.display)
}

/** Shared lazy initialization; callers can retain the converter for synchronous rendering. */
export function loadMathJax(): Promise<MathJaxEngine> {
  enginePromise ??= loadEngine()
  return enginePromise
}

async function loadEngine(): Promise<MathJaxEngine> {
  const [
    { mathjax },
    { TeX },
    { SVG },
    { liteAdaptor },
    { RegisterHTMLHandler },
    { AllPackages },
  ] = await Promise.all([
    import('mathjax-full/js/mathjax.js'),
    import('mathjax-full/js/input/tex.js'),
    import('mathjax-full/js/output/svg.js'),
    import('mathjax-full/js/adaptors/liteAdaptor.js'),
    import('mathjax-full/js/handlers/html.js'),
    import('mathjax-full/js/input/tex/AllPackages.js'),
  ])
  Reflect.set(globalThis, '__mathRenderingMathJaxLoaded', true)
  const adaptor = liteAdaptor()
  RegisterHTMLHandler(adaptor)
  const document = mathjax.document('', {
    InputJax: new TeX({ packages: AllPackages }),
    OutputJax: new SVG({ fontCache: 'none' }),
  })

  return {
    convert(tex, display) {
      const container = document.convert(tex, { display }) as LiteElement
      const svgNode = adaptor.firstChild(container)
      if (!svgNode || !('attributes' in svgNode)) {
        throw new Error('MathJax returned no SVG')
      }
      // MathJax's SVG stylesheet, explicit so host resets cannot make the SVG a block.
      adaptor.setStyle(svgNode, 'display', 'inline')
      adaptor.setStyle(svgNode, 'overflow', 'visible')
      adaptor.setStyle(svgNode, 'min-width', '1px')
      adaptor.setStyle(svgNode, 'min-height', '1px')
      const svg = adaptor.outerHTML(svgNode)
      return {
        svg,
        widthEx: parseEx(svgNode.attributes['width'] ?? null, 'width'),
        heightEx: parseEx(svgNode.attributes['height'] ?? null, 'height'),
        verticalAlignEx: parseVerticalAlign(svgNode.attributes['style'] ?? null),
      }
    },
  }
}

function parseEx(value: unknown, name: string): number {
  const match = typeof value === 'string' ? value.trim().match(/^(-?(?:\d+(?:\.\d*)?|\.\d+))ex$/) : null
  if (!match) throw new Error(`MathJax SVG ${name} is not in ex units`)
  return Number(match[1])
}

function parseVerticalAlign(style: unknown): number {
  const match = typeof style === 'string' ? style.match(
    /(?:^|;)\s*vertical-align\s*:\s*(-?(?:\d+(?:\.\d*)?|\.\d+))ex(?:;|$)/i,
  ) : null
  return match ? Number(match[1]) : 0
}
